import * as _ from 'lodash';
import { IDonorProfilePostTransplant } from '@nkr/models/donor-profile';
import { Button, Col, ControlLabel, Form, FormControl, FormGroup, Label, Panel, Table } from 'react-bootstrap';
import FileUpload from '@nkr/components/file-upload';

interface IProps {
    data: IDonorProfilePostTransplant
    newCompletedFormsAndChecklistsFile: File,
    handleDataChange: (data: IDonorProfilePostTransplant) => void,
    onChangeFile: (file: File) => void,
    onRemoveFile: () => void,
    onViewFile: () => void,
}

const labelReturn = (val: boolean | null) => {
    if (val === null) {
        return <Label>Unknown</Label>
    }

    if (val === true) {
        return <Label bsStyle='success'>Yes</Label>
    }

    if (val === false) {
        return <Label className='label-important'>No</Label>
    }
}

const Content = (props: IProps) => {
    if (props.data == null)
        return null;

    const handleSelectChange = (e: React.ChangeEvent<FormControl & HTMLSelectElement>) => {
        let { name, value } = e.currentTarget;
        let data = _.cloneDeep(props.data);

        data[name] = value;

        props.handleDataChange(data);
    };

    const handleFieldChange = (e: React.ChangeEvent<FormControl & HTMLInputElement>) => {
        let { name, value } = e.currentTarget;
        let data = _.cloneDeep(props.data);

        data[name] = value;

        props.handleDataChange(data);
    };

    const handleFileDropped = (acceptedFiles: File[]) => {
        props.onChangeFile(acceptedFiles[0]);
    };

    const handleRemoveFile = () => {
        props.onRemoveFile();
    };

    const handleViewFile = () => {
        props.onViewFile();
    }

    return (
        <>
            <div className='container-fluid donor-profile-section donor-profile-content'>
                <Panel>
                    <Panel.Heading>Details</Panel.Heading>
                    <Panel.Body>
                        <Form horizontal>
                            <FormGroup>
                                <Col xs={12} lg={2} componentClass={ControlLabel}>Which kidney was donated:</Col>
                                <Col xs={2} lg={2}>
                                    <FormControl
                                        componentClass='select'
                                        style={{ width: 'calc(100% + 20px)' }}
                                        id='whichKidney-1'
                                        name='whichKidneyDonated'
                                        value={props.data.whichKidneyDonated}
                                        onChange={handleSelectChange}>
                                        <option value={'Unknown'}>Unknown</option>
                                        <option value={'Left'}>Left</option>
                                        <option value={'Right'}>Right</option>
                                    </FormControl>
                                </Col>
                            </FormGroup>

                            <FormGroup>
                                <Col xs={12} lg={2} componentClass={ControlLabel}>Cross-Clamp Date/Time:</Col>
                                <Col xs={2} lg={2}>
                                    <FormControl
                                        type='datetime-local'
                                        style={{ width: 'calc(100% + 20px)' }}
                                        name='crossClampDateTime'
                                        value={props.data.crossClampDateTime}
                                        onChange={handleFieldChange} />
                                </Col>
                            </FormGroup>

                            <FormGroup>
                                <Col xs={12} lg={2} componentClass={ControlLabel}>Completed Forms &amp; Checklists:</Col>
                                <Col xs={2} lg={3}>
                                    <FileUpload
                                        label='Click here to select the file or drag &amp; drop the file here...'
                                        files={props.newCompletedFormsAndChecklistsFile != null ? [props.newCompletedFormsAndChecklistsFile] : []}
                                        height='30px'
                                        accept='.pdf'
                                        onFileDropped={handleFileDropped}
                                        singleUploadOnly={true}
                                        onRemoveFile={handleRemoveFile} />
                                </Col>
                                {props.data.completedFormsAndChecklistsUploadPath != '' && (
                                    <Col xs={2} lg={2}>
                                        <Button bsStyle='link' onClick={handleViewFile}>View Existing File</Button>
                                    </Col>
                                )}
                            </FormGroup>
                        </Form>
                    </Panel.Body>
                </Panel>

                <Panel>
                    <Panel.Heading>Shared Recipient Status</Panel.Heading>
                    <Panel.Body>
                        <Form horizontal>
                            <FormGroup>
                                <Col xs={12} lg={5} componentClass={ControlLabel}>Is this recipient potentially willing to remove anonymity (e.g. might be willing to meet donor if asked): </Col>
                                <Col xs={12} lg={6}>
                                    <div className='form-control-static'>
                                        {labelReturn(props.data.isWillingToMeetDonor)}
                                    </div>
                                </Col>
                            </FormGroup>

                            <FormGroup>
                                <Col xs={12} lg={5} componentClass={ControlLabel}>Is this recipient willing to share Graft Survival and Creatinine Info: </Col>
                                <Col xs={12} lg={6}>
                                    <div className='form-control-static'>
                                        {labelReturn(props.data.isSharingGfAndCreatinine)}
                                    </div>
                                </Col>
                            </FormGroup>

                            {props.data.isSharingGfAndCreatinine === true && (
                                <>
                                    <FormGroup>
                                        <Col xs={12} lg={5} componentClass={ControlLabel}> Graft Survival? </Col>
                                        <Col xs={12} lg={6}>
                                            <div className='form-control-static'>
                                                {labelReturn(props.data.graftSurvival)}
                                            </div>
                                        </Col>
                                    </FormGroup>
                                    <FormGroup>
                                        <Col xs={12} lg={5} componentClass={ControlLabel}>Latest Creatinine: </Col>
                                        <Col xs={12} lg={6}>
                                            <div className='form-control-static'>
                                                <Label>{props.data.creatinineValue}</Label> from {props.data.creatinineDate}
                                            </div>
                                        </Col>
                                    </FormGroup>
                                </>
                            )}
                        </Form>
                    </Panel.Body>
                </Panel>

                <Panel>
                    <Panel.Heading>Recipient Financial Info</Panel.Heading>
                    <Panel.Body>
                        <Table bordered condensed>
                            <tbody>
                                <tr>
                                    <td style={{ fontWeight: 600 }}>Insurance:</td>
                                    <td>{props.data.insurance}</td>
                                    <td style={{ fontWeight: 600 }}>Subscriber if Not Patient:</td>
                                    <td>{props.data.relationshipToSubscriber}</td>
                                    <td style={{ fontWeight: 600 }}>Global Case Rate:</td>
                                    <td>{props.data.globalCaseRate == true ? 'Yes' : 'No'}</td>
                                </tr>
                                <tr>
                                    <td style={{ fontWeight: 600 }}>Policy #:</td>
                                    <td>{props.data.policyIdNumber}</td>
                                    <td style={{ fontWeight: 600 }}>Group ID:</td>
                                    <td>{props.data.groupNumber}</td>
                                    <td style={{ fontWeight: 600 }}>Professional Case Rate:</td>
                                    <td>{props.data.professionalCaseRate == true ? 'Yes' : 'No'}</td>
                                </tr>
                                <tr>
                                    <td style={{ fontWeight: 600 }}>Insurance Last Updated:</td>
                                    <td>{props.data.lastUpdated}</td>
                                    <td style={{ fontWeight: 600 }}>Precert. Phone:</td>
                                    <td>{props.data.precertificationPhone}</td>
                                    <td></td>
                                    <td></td>
                                </tr>
                                <tr>
                                    <td style={{ fontWeight: 600 }}>Benefit Phone:</td>
                                    <td>{props.data.benefitPhone}</td>
                                    <td style={{ fontWeight: 600 }}>Case Manager Name:</td>
                                    <td>{props.data.caseManagerName}</td>
                                    <td style={{ fontWeight: 600 }}>Case Manager Phone:</td>
                                    <td>{props.data.caseManagerPhone}</td>
                                </tr>
                            </tbody>
                        </Table>
                    </Panel.Body>
                </Panel>
            </div>
        </>
    )
}

export default Content;